import { FC, Fragment, memo, useCallback } from 'react';
import { useClearRefinements, useCurrentRefinements } from 'react-instantsearch';
import {
    CurrentRefinementsConnectorParamsRefinement,
    CurrentRefinementsConnectorParamsItem,
} from 'instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements';
import { TranslationKey } from '~/lib';
import { useTranslation } from '~/shared/utils/translation';
import {
    StyledList,
    StyledFacet,
    StyledCloseIcon,
    StyledClearAll,
    StyledSwatch,
} from './SelectedFacets.styles';
import { useDeferredFunction } from '~/shared/hooks/useDeferredFunction';
import { Facets, STOCK_FACETS } from '~/services/instantsearch';
import { SwatchColor } from '~/shared/components';
import useColorSwatches from '~/features/productList/hooks/useColorSwatches';
import { useStoreAvailability } from '~/features/productList/hooks/useStoreAvailability';

interface SelectedFacetItemProps {
    item: CurrentRefinementsConnectorParamsItem;
}

const SelectedFacetItem: FC<SelectedFacetItemProps> = ({ item }) => {
    const { translate } = useTranslation();
    const { colorSwatches } = useColorSwatches();
    const { indexName, refine, refinements } = item;
    const isColorFacet = item.attribute === Facets.COLOR;

    const getRefinementLabel = useCallback(
        (refinement: CurrentRefinementsConnectorParamsRefinement) => {
            if (['true', 'false', 'True', 'False'].includes(refinement.label)) {
                return translate(`plp.facet.${refinement.attribute}` as TranslationKey);
            }

            return refinement.label;
        },
        [],
    );

    const deferredRefine = useDeferredFunction(refine);

    const removeRefinement = useCallback(
        (refinement: CurrentRefinementsConnectorParamsRefinement) => {
            deferredRefine(refinement);
        },
        [],
    );

    return (
        <Fragment>
            {refinements.map((refinement) => (
                <StyledFacet
                    key={`${indexName}-${refinement.value}`}
                    onClick={() => removeRefinement(refinement)}
                    tabIndex={0}
                    onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            removeRefinement(refinement);
                        }
                    }}
                >
                    {isColorFacet && (
                        <StyledSwatch>
                            <SwatchColor
                                src={
                                    colorSwatches?.find((swatch) => swatch.key === refinement.value)
                                        ?.imageUrl
                                }
                                alt=""
                                bordered
                            />
                        </StyledSwatch>
                    )}
                    {getRefinementLabel(refinement)}
                    <StyledCloseIcon />
                </StyledFacet>
            ))}
        </Fragment>
    );
};

const MemoizedSelectedFacetItem = memo(SelectedFacetItem);

const SelectedFacets: FC = () => {
    const { translate } = useTranslation();
    const { refine } = useClearRefinements();
    const { items: currentRefinements } = useCurrentRefinements({
        // We will exclude items that will be presented differently.
        excludedAttributes: ['query', ...STOCK_FACETS],
    });
    const clearRefinements = useDeferredFunction(refine, []);

    const {
        isOnlineEnabled,
        isStoreEnabled,
        selectedStores,
        setIsOnlineEnabled,
        setIsStoreEnabled,
        toggleStore,
    } = useStoreAvailability();

    if (
        !currentRefinements.length &&
        !selectedStores.length &&
        !isOnlineEnabled &&
        !isStoreEnabled
    ) {
        return null;
    }

    return (
        <StyledList>
            <StyledClearAll
                onClick={clearRefinements}
                tabIndex={0}
                onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        clearRefinements();
                    }
                }}
            >
                {translate('plp.clearFilter')}
                <StyledCloseIcon />
            </StyledClearAll>
            {currentRefinements.map((item) => (
                <MemoizedSelectedFacetItem key={item.attribute} item={item} />
            ))}
            {isOnlineEnabled && (
                <StyledFacet
                    onClick={() => setIsOnlineEnabled(false)}
                    tabIndex={0}
                    onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setIsOnlineEnabled(false);
                        }
                    }}
                >
                    {translate('plp.storeAvailability.online')}
                    <StyledCloseIcon />
                </StyledFacet>
            )}
            {isStoreEnabled && (
                <StyledFacet
                    onClick={() => setIsStoreEnabled(false)}
                    tabIndex={0}
                    onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setIsStoreEnabled(false);
                        }
                    }}
                >
                    {translate('plp.storeAvailability.selectStore')}
                    <StyledCloseIcon />
                </StyledFacet>
            )}
            {selectedStores.map((store) => (
                <StyledFacet
                    key={`store-${store}`}
                    onClick={() => toggleStore(store)}
                    tabIndex={0}
                    onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleStore(store);
                        }
                    }}
                >
                    {store}
                    <StyledCloseIcon />
                </StyledFacet>
            ))}
        </StyledList>
    );
};

export default memo(SelectedFacets);
